import React, { useState } from 'react';
import {
  Search,
  Package,
  Filter,
  Layers,
  Sparkles,
  Tag,
  Boxes,
  Info,
  ChevronRight,
  TrendingUp,
  Coins
} from 'lucide-react';
import { Product } from '../types';

interface ProductInventoryListProps {
  availableProducts: Product[];
  onSelectForTransparent?: (productCode: string) => void;
}

export const ProductInventoryList: React.FC<ProductInventoryListProps> = ({
  availableProducts = [],
  onSelectForTransparent
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Categories list
  const categories = [
    { id: 'all', name: 'ทั้งหมด' },
    { id: 'health', name: 'สุขภาพ (Health)' },
    { id: 'beauty', name: 'ความงาม (Beauty)' },
    { id: 'cosmetics', name: 'เครื่องสำอาง (Cosmetics)' },
    { id: 'home', name: 'ของใช้ในครัวเรือน (Home)' },
    { id: 'food', name: 'อาหาร (Food)' },
    { id: 'agriculture', name: 'เกษตรกรรม (Agriculture)' }
  ];

  // Filter products
  const filteredProducts = availableProducts.filter((product) => {
    if (!product) return false;
    const nameStr = String(product.name || '').toLowerCase();
    const codeStr = String(product.code || '');
    const query = searchQuery.trim().toLowerCase();

    const matchesSearch =
      nameStr.includes(query) ||
      codeStr.toLowerCase().includes(query);

    const matchesCategory =
      selectedCategory === 'all' || product.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  // Calculate some simple stats
  const totalProducts = availableProducts.length;
  const healthCount = availableProducts.filter(p => p?.category === 'health').length;
  const beautyCount = availableProducts.filter(p => p?.category === 'beauty' || p?.category === 'cosmetics').length;

  return (
    <div className="space-y-6 animate-in fade-in duration-200">
      {/* Overview Stats Headers */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Total Products Card */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs text-slate-500 font-bold">รายการสินค้าทั้งหมด</span>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              {totalProducts.toLocaleString()} <span className="text-xs font-normal text-slate-400">รายการ</span>
            </div>
          </div>
          <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
            <Boxes className="w-6 h-6" />
          </div>
        </div>

        {/* Health Products Card */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs text-slate-500 font-bold">กลุ่มสินค้าเพื่อสุขภาพ</span>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              {healthCount.toLocaleString()} <span className="text-xs font-normal text-slate-400">รายการ</span>
            </div>
          </div>
          <div className="w-12 h-12 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center shrink-0">
            <TrendingUp className="w-6 h-6" />
          </div>
        </div>

        {/* Beauty/Cosmetics Products Card */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-2xs flex items-center justify-between">
          <div className="space-y-1">
            <span className="text-xs text-slate-500 font-bold">กลุ่มความงามและของใช้</span>
            <div className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">
              {beautyCount.toLocaleString()} <span className="text-xs font-normal text-slate-400">รายการ</span>
            </div>
          </div>
          <div className="w-12 h-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <Tag className="w-6 h-6" />
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs space-y-4">
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="ค้นหาตามรหัส หรือชื่อสินค้าในคลังสินค้า..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
            />
          </div>

          {/* Category Filter Desktop/Dropdown */}
          <div className="flex items-center gap-2">
            <Filter className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="bg-slate-50 border border-slate-200 text-slate-800 text-xs rounded-xl px-3 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/20 transition-all"
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  หมวดหมู่: {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick Horizontal Scroll Categories on Mobile */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-2 px-2 scrollbar-none md:hidden">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3 py-1 rounded-full text-[10px] font-bold whitespace-nowrap border transition-all ${
                selectedCategory === cat.id
                  ? 'bg-teal-600 border-teal-600 text-white shadow-3xs'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Products Display */}
      {filteredProducts.length === 0 ? (
        <div className="py-16 text-center bg-white rounded-2xl border border-dashed border-slate-200 p-8">
          <Package className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm font-bold text-slate-700">ไม่พบสินค้าในระบบ</p>
          <p className="text-xs text-slate-400 mt-1">
            ลองปรับเปลี่ยนคำค้นหา หรือเปลี่ยนตัวกรองหมวดหมู่ใหม่อีกครั้ง
          </p>
        </div>
      ) : (
        <>
          {/* Table on Desktop */}
          <div className="hidden md:block bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-600 font-bold text-[11px] uppercase">
                    <th className="p-4 w-16 text-center">รูปภาพ</th>
                    <th className="p-4 w-24">รหัสสินค้า</th>
                    <th className="p-4">ชื่อสินค้า</th>
                    <th className="p-4 w-32">หมวดหมู่</th>
                    <th className="p-4 w-28 text-right">ราคาขายส่ง</th>
                    <th className="p-4 w-28 text-right">ราคาขายปลีก</th>
                    <th className="p-4 w-24 text-center">คะแนน (PV)</th>
                    {onSelectForTransparent && <th className="p-4 w-36 text-center">คลังภาพไลฟ์</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="p-3">
                        <div className="w-10 h-10 rounded-lg bg-slate-50 border border-slate-200/80 flex items-center justify-center overflow-hidden shrink-0">
                          {product.image ? (
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <Package className="w-5 h-5 text-slate-400" />
                          )}
                        </div>
                      </td>
                      <td className="p-3 font-mono font-bold text-slate-800">
                        #{product.code}
                      </td>
                      <td className="p-3">
                        <div className="font-extrabold text-slate-800 text-xs">{product.name}</div>
                        {product.volume && (
                          <span className="text-[10px] text-slate-400 font-medium">ปริมาณ: {product.volume}</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold uppercase">
                          {categories.find(c => c.id === product.category)?.name || product.category}
                        </span>
                      </td>
                      <td className="p-3 text-right font-mono font-extrabold text-teal-700">
                        ฿{product.memberPrice?.toLocaleString() || product.price?.toLocaleString()}
                      </td>
                      <td className="p-3 text-right font-mono font-medium text-slate-500">
                        ฿{product.price?.toLocaleString()}
                      </td>
                      <td className="p-3 text-center">
                        <span className="px-2 py-0.5 rounded-md bg-amber-50 text-amber-800 text-[10px] font-black border border-amber-100 font-mono">
                          {product.pv} PV
                        </span>
                      </td>
                      {onSelectForTransparent && (
                        <td className="p-3 text-center">
                          <button
                            onClick={() => onSelectForTransparent(product.code)}
                            className="px-3 py-1.5 bg-[#BE185D] hover:bg-pink-800 text-white rounded-lg text-[10px] font-extrabold flex items-center gap-1 mx-auto transition-all shadow-3xs cursor-pointer active:scale-95"
                          >
                            <Sparkles className="w-3 h-3" />
                            <span>ตัดภาพโปร่งแสง</span>
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cards on Mobile */}
          <div className="grid grid-cols-1 gap-3.5 md:hidden">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl border border-slate-200 p-4 shadow-3xs space-y-3"
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[9px] font-bold uppercase">
                      #{product.code}
                    </span>
                    <h4 className="font-extrabold text-slate-800 text-xs mt-1 leading-snug line-clamp-2">
                      {product.name}
                    </h4>
                    {product.volume && (
                      <p className="text-[10px] text-slate-400 font-medium mt-0.5">ปริมาณ: {product.volume}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 bg-slate-50/80 p-2.5 rounded-xl border border-slate-100 text-center">
                  <div>
                    <span className="text-[9px] text-slate-400 block font-bold">ราคาสมาชิก</span>
                    <span className="font-mono font-black text-teal-700 text-xs">
                      ฿{product.memberPrice?.toLocaleString() || product.price?.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-[9px] text-slate-400 block font-bold">ราคาปลีก</span>
                    <span className="font-mono font-bold text-slate-500 text-xs">
                      ฿{product.price?.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-[9px] text-slate-400 block font-bold">คะแนน PV</span>
                    <span className="font-mono font-black text-amber-700 text-xs">
                      {product.pv} PV
                    </span>
                  </div>
                </div>

                {onSelectForTransparent && (
                  <button
                    onClick={() => onSelectForTransparent(product.code)}
                    className="w-full py-2 bg-[#BE185D] hover:bg-pink-800 text-white rounded-xl text-xs font-extrabold flex items-center justify-center gap-1.5 transition-all shadow-3xs"
                  >
                    <Sparkles className="w-3.5 h-3.5" />
                    <span>สร้างภาพโปร่งแสงสำหรับไลฟ์</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
